import { buyoverTiersToGoals } from './buyoverTiers'
import { type GoalFacts, type NormalizedDiscountData } from './discountSteps'
import { type GroupPricingResult } from './groupCheckoutPricing.types'

// Shipping is a goal of its own, never part of the main discount.
export type MainGoalFacts = Exclude<GoalFacts, { goal: 'shipping' }>

// A bundle repeats, so it steps like a buyover ladder: bundles already
// charged are one reached goal, the next bundle is the goal after it.
function bundlesToGoals(
  bundleData: NonNullable<GroupPricingResult['bundleData']>,
  getBundleCommunicationEnabled: () => boolean
): MainGoalFacts[] {
  const {
    bundleTiles: singleBundleTilesCount,
    bundlePrice: singleBundlePrice,
    howManyToBundlePrice,
    applied,
    appliedBundleCount: appliedBundlesCount,
  } = bundleData

  if (appliedBundlesCount === 0 || !getBundleCommunicationEnabled()) {
    return [
      {
        goal: 'bundle',
        target: singleBundleTilesCount,
        remaining: howManyToBundlePrice,
        applied,
        singleBundleTilesCount,
        singleBundlePrice,
        appliedBundlesCount: 1,
      },
    ]
  }

  const appliedBundleDiscount: MainGoalFacts = {
    goal: 'bundle',
    target: singleBundleTilesCount * appliedBundlesCount,
    remaining: 0,
    applied: true,
    singleBundleTilesCount,
    singleBundlePrice,
    appliedBundlesCount,
  }
  const nextBundleDiscount: MainGoalFacts = {
    goal: 'bundle',
    target: singleBundleTilesCount * (appliedBundlesCount + 1),
    remaining: howManyToBundlePrice || singleBundleTilesCount,
    applied: false,
    singleBundleTilesCount,
    singleBundlePrice,
    appliedBundlesCount: appliedBundlesCount + 1,
  }

  return [appliedBundleDiscount, nextBundleDiscount]
}

// The discount the order is working toward: a buyover ladder (one goal per
// tier), a bundle, or a flat percent — in priority order.
export function getMainGoals(
  data: Pick<
    GroupPricingResult,
    | 'buyoverData'
    | 'bundleData'
    | 'percentOff'
    | 'productTotalBeforeDiscount'
    | 'productTotalAfterDiscount'
  >,
  getBundleCommunicationEnabled: () => boolean
): MainGoalFacts[] | undefined {
  const { buyoverData, bundleData, percentOff } = data
  const totalSaved =
    data.productTotalBeforeDiscount - data.productTotalAfterDiscount

  if (buyoverData) {
    return buyoverTiersToGoals(buyoverData.tiers, totalSaved)
  }
  if (bundleData && bundleData.bundleTiles > 0) {
    return bundlesToGoals(bundleData, getBundleCommunicationEnabled)
  }
  if (percentOff) {
    return [{ goal: 'percent', applied: true, percentOff }]
  }
  return undefined
}

// Maps the normalized group-checkout pricing into the `NormalizedDiscountData`
// the discount stepper consumes.
export function groupPricingToDiscountData(
  groupPricing: GroupPricingResult,
  includeShipping: boolean,
  getBundleCommunicationEnabled: () => boolean
): NormalizedDiscountData {
  const { currency, shippingBuyoverData } = groupPricing

  const shipping =
    includeShipping && shippingBuyoverData
      ? {
          target: shippingBuyoverData.buyOverPrice,
          remaining: shippingBuyoverData.missingAmountForDiscount,
          applied: shippingBuyoverData.applied,
        }
      : undefined

  return {
    currency,
    shipping,
    mainGoals: getMainGoals(groupPricing, getBundleCommunicationEnabled),
  }
}
